import { AxiosError } from 'axios';
import BlockchainError from 'blockchain/BlockchainError.ts';
import { VAULT_URL_PATH } from 'common/constants.ts';
import { BaseError, errorCodes, errorMessages } from 'common/errors/index.ts';
import { addHexPrefix, computeHash, encryptDocument, isNetworkError, MPOST, sanitizeFilename } from 'common/helpers.ts';
import React from 'react';
import useSWRMutation from 'swr/mutation';
import { useBlockchain } from '#admin/hook/useBlockchain.tsx';
import { useEnv } from '#admin/hook/useEnv.tsx';

export type FileMetadata = Record<string, string>;

export type Upload = {
  type: 'generic' | 'poc' | 'pod' | 'signature';
  signerName: string;
  file: File;
  metadata?: FileMetadata;
  comments?: string;
};

export type UploadedFile = Upload & {
  url: string;
};

export type UploadFailure = {
  upload: Upload;
  error: Error;
};

export type UploadResult = {
  file: File;
  url?: string;
  error?: Error;
};

export type UseUploadFileOptions = {
  onSuccess?: (uploadedFiles: UploadedFile[]) => void | Promise<void>;
  onError?: (failures: UploadFailure[]) => void | Promise<void>;
};

export function useUploadFile(me, options: UseUploadFileOptions = {}) {
  const env = useEnv();
  const { vault } = useBlockchain();
  const [isUploading, setIsUploading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isUploaded, setIsUploaded] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [url, setUrl] = React.useState<string>('');
  const networkError = React.useMemo(() => isNetworkError(error), [error]);

  const { trigger: triggerPresignedUrl } = useSWRMutation('/storage/presigned-url', MPOST);
  const { trigger: triggerCreateDocument } = useSWRMutation('/document', MPOST);

  const uploadOne = React.useCallback(
    async (upload: Upload): Promise<{ url?: string; error?: Error }> => {
      const { type, signerName, file, metadata = {}, comments } = upload;
      let url: string | undefined;
      let presignedURL: string | undefined;
      let caughtError: Error | undefined;

      if (!me || !vault || !env || !file.size) return { url, error: caughtError };

      setErrorMessage('');
      setUrl('');

      try {
        const filename = sanitizeFilename(file.name);
        const docArrayBuffer = await file.arrayBuffer();

        //////////////////////////////
        // ENCRYPTION + PRESIGNED URL
        //////////////////////////////

        // Calculate SHA256 of the file:
        const hashString = await computeHash(docArrayBuffer);

        const { isValid: docAlreadyExistsOnTheBlockchain } = await vault.verifyDocument(hashString);

        // Get presigned URL:
        try {
          presignedURL = await triggerPresignedUrl({ filename: hashString });
          if (!presignedURL) throw new BaseError(errorCodes.STORAGE_FAILED_TO_GET_PRESIGNED_URL, errorMessages.STORAGE_FAILED_TO_GET_PRESIGNED_URL);
          url = `${new URL(presignedURL).origin}/${VAULT_URL_PATH}/${hashString}`;
        } catch (e) {
          if (e instanceof AxiosError) {
            setError(e);
            e.message = e.response?.data?.message || 'Failed to get presigned URL';
          }
          throw e;
        }

        if (!docAlreadyExistsOnTheBlockchain) {
          // Generate random key and encrypt file:
          const { encryptionKey, encryptedDocument } = await encryptDocument(docArrayBuffer);

          //////////////////////////////
          // VAULT UPLOAD
          //////////////////////////////
          try {
            // Send encrypted file to storage (not using axios because of the interceptors)
            await fetch(presignedURL, {
              headers: { Accept: 'application/xml', 'content-type': 'application/octet-stream', 'x-ms-blob-type': 'BlockBlob' },
              body: encryptedDocument,
              mode: 'cors',
              method: 'PUT',
            });
          } catch (e) {
            if (e instanceof Error) {
              console.error('Failed to upload file to storage:', e.message);
              throw new Error('Failed to upload file to storage. Please try again later.');
            }
            throw e;
          }

          //////////////////////////////
          // SMART CONTRACT
          //////////////////////////////
          try {
            const hash = addHexPrefix(hashString);

            const document = {
              encryptionAlg: 'AES-CBC',
              encryptionKey,
              hash,
              hashAlg: 'SHA-256',
              url,
              metadata: [
                { key: 'filename', value: filename },
                { key: 'type', value: type },
                { key: 'signer_name', value: signerName },
                ...Object.entries(metadata).map(([key, value]) => ({ key, value })),
              ],
              ownerId: me.id,
            };

            // we need to create the document and at the same time give permission to both the admin
            // (so APIGW can align permissions as needed) and the driver (so he can see the document right after upload)
            await vault.createDocumentAndPermissions(document, [env.SUPERADMIN_GROUP_ID, me.id]);
          } catch (e) {
            console.error(e);
            if (e instanceof BlockchainError && e.errorName !== 'VaultDocumentAlreadyExists') {
              throw new Error('Failed to save document to blockchain. Please try again.');
            }
          }
        }

        //////////////////////////////
        // ATLAS
        //////////////////////////////
        await triggerCreateDocument({ url: presignedURL, filename, name: filename, type, comments, signer_name: signerName });

        setUrl(url);
        setIsUploaded(true);
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError) {
          setErrorMessage(error.response?.data?.message || error.message);
          setError(error);
          caughtError = error;
        } else if (error instanceof Error) {
          setErrorMessage(error.message);
          setError(error);
          caughtError = error;
        }
      }

      return { url, error: caughtError };
    },
    [me, triggerPresignedUrl, triggerCreateDocument, vault, env],
  );

  const upload = React.useCallback(
    async (arg: Upload | Upload[]): Promise<UploadResult[]> => {
      const uploads = Array.isArray(arg) ? arg : [arg];
      const { onSuccess, onError } = options;

      setIsUploading(true);

      // Sequential — each file needs its own Vault smart-contract tx.
      const results: UploadResult[] = [];
      for (const upload of uploads) {
        const { url, error } = await uploadOne(upload);
        results.push({ file: upload.file, url, error });
      }

      setIsUploading(false);

      const uploadedFiles: UploadedFile[] = [];
      const failures: UploadFailure[] = [];
      for (let i = 0; i < results.length; i++) {
        const { url, error } = results[i];
        if (error) failures.push({ upload: uploads[i], error });
        else if (url) uploadedFiles.push({ ...uploads[i], url });
      }

      if (uploadedFiles.length > 0) await onSuccess?.(uploadedFiles);
      if (failures.length > 0) await onError?.(failures);

      return results;
    },
    [uploadOne, options],
  );

  return { upload, isUploading, errorMessage, isUploaded, error, url, networkError };
}
